import React from 'react';
import { ToggleButton, ToggleButtonGroup, Typography, Paper } from '@mui/material';
import AssignmentIcon from '@mui/icons-material/Assignment';
import InventoryIcon from '@mui/icons-material/Inventory';
import ScienceIcon from '@mui/icons-material/Science';
import { WorkflowFlowType } from '../../../types/workflow';

interface FlowTypeSelectorProps {
  value: WorkflowFlowType;
  onChange: (flowType: WorkflowFlowType) => void;
  disabled?: boolean;
}

/**
 * FlowTypeSelector Component
 *
 * A toggle button group for selecting the workflow creation path:
 * - "NewRequest" (Path A): Standard workflow — assign farmer, farm, and entity roles
 * - "LabTriggered" (Path C): Lab-initiated workflow — entity resolution via CSV auto-resolve
 * - "ExistingBatch" (Path B): Link a pre-existing batch to a new workflow
 */
export const FlowTypeSelector: React.FC<FlowTypeSelectorProps> = ({
  value,
  onChange,
  disabled = false,
}) => {
  const handleChange = (
    _: React.MouseEvent<HTMLElement>,
    newFlowType: WorkflowFlowType | null
  ) => {
    if (newFlowType) {
      onChange(newFlowType);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
      <Typography variant="subtitle2" color="primary" gutterBottom>
        Workflow Type
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Choose the workflow creation path.
      </Typography>
      <ToggleButtonGroup
        value={value}
        exclusive
        onChange={handleChange}
        aria-label="workflow flow type"
        fullWidth
        disabled={disabled}
      >
        <ToggleButton value="LabTriggered" aria-label="lab triggered">
          <ScienceIcon sx={{ mr: 1 }} />
          Lab-Triggered
        </ToggleButton>
        <ToggleButton value="NewRequest" aria-label="new request">
          <AssignmentIcon sx={{ mr: 1 }} />
          New Request
        </ToggleButton>
        <ToggleButton value="ExistingBatch" aria-label="existing batch">
          <InventoryIcon sx={{ mr: 1 }} />
          Existing Batch
        </ToggleButton>
      </ToggleButtonGroup>
    </Paper>
  );
};

export default FlowTypeSelector;
